import { useEffect, useState } from 'react';

import {
  getClientCapabilities,
  RECORDING_REMOTE_TOOL_ACTION,
} from './clientCapabilities';

/**
 * UI availability resolves through the same native declaration used by
 * Project request payloads. Stays false while resolving. Probes per mount
 * rather than sharing a cached resolution: the value is fixed per app session
 * (the Rust catalog is per-build), so the cost is one IPC call per composer
 * mount, and a transient bridge failure self-corrects on the next mount
 * instead of being pinned for as long as any consumer stays mounted.
 */
export const useIsProjectRecordingAvailableCore = (
  resolveRecordingAvailable: () => Promise<boolean>
): boolean => {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    let active = true;
    void resolveRecordingAvailable().then((result) => {
      if (active) setAvailable(result);
    });
    return () => {
      active = false;
    };
  }, [resolveRecordingAvailable]);

  return available;
};

const resolveRecordingAvailable = async () => {
  const caps = await getClientCapabilities({ advertiseRecordingRemoteTool: true });
  return caps?.remote_tools?.actions.includes(RECORDING_REMOTE_TOOL_ACTION) === true;
};

export const useIsProjectRecordingAvailable = () =>
  useIsProjectRecordingAvailableCore(resolveRecordingAvailable);
